import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import { useCallback, useEffect, useRef, useState } from 'react';

// Voice capture for chat and creator inputs. The mic stream feeds an
// AnalyserNode so the UI can render real input levels; audio is recorded with
// MediaRecorder and sent to forge for transcription when the user accepts.
// Cancel and unmount discard everything without a request.

export type VoiceRecorderState = 'idle' | 'recording' | 'transcribing';

interface UseVoiceRecorderOptions {
	// fires once with the transcript when a recording completes; skipped when
	// the audio held no speech
	onTranscript: (text: string) => void;
	// fires when a recording begins, before any transcript callbacks
	onRecordStart?: () => void;
}

// hitting the cap finishes the recording (transcribing what was captured)
// rather than failing; the UI counts down as it approaches
export const MAX_RECORDING_MS = 300_000;

// caps upload size: five minutes at this bitrate stays a small payload even as
// base64, for opus and AAC alike
const AUDIO_BITS_PER_SECOND = 64_000;

const pickMimeType = (): string | undefined => {
	if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
	if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
	if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
	return undefined;
};

export const isVoiceSupported = (): boolean =>
	typeof navigator !== 'undefined' &&
	Boolean(navigator.mediaDevices?.getUserMedia) &&
	typeof MediaRecorder !== 'undefined';

const blobToBase64 = (blob: Blob): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = reader.result as string;
			resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});

export const useVoiceRecorder = ({ onTranscript, onRecordStart }: UseVoiceRecorderOptions) => {
	const [state, setState] = useState<VoiceRecorderState>('idle');
	const [elapsedMs, setElapsedMs] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const recorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const streamRef = useRef<MediaStream | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	// read by the waveform UI at animation rate; null while idle
	const analyserRef = useRef<AnalyserNode | null>(null);
	// bumped on cancel/unmount so an in-flight transcription result is dropped
	const sessionRef = useRef(0);
	const startedAtRef = useRef(0);
	const tickerRef = useRef<number | null>(null);

	const clearTicker = () => {
		if (tickerRef.current !== null) {
			window.clearInterval(tickerRef.current);
			tickerRef.current = null;
		}
	};

	const teardownAudio = () => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
		audioContextRef.current?.close().catch(() => {});
		audioContextRef.current = null;
		analyserRef.current = null;
	};

	const finish = useCallback(
		async () => {
			const recorder = recorderRef.current;
			if (!recorder) return;
			recorderRef.current = null;
			clearTicker();
			const session = sessionRef.current;

			// wait for the recorder to flush buffered audio into ondataavailable
			const stopped = new Promise<void>((resolve) => {
				recorder.onstop = () => resolve();
			});
			if (recorder.state !== 'inactive') recorder.stop();

			setState('transcribing');
			try {
				await stopped;
				// the recorder reports the mime it actually produced
				const mimeType = recorder.mimeType || 'audio/webm';
				const blob = new Blob(chunksRef.current, { type: mimeType });
				chunksRef.current = [];
				// release the mic as soon as capture ends, not after the round-trip
				teardownAudio();
				const data = await blobToBase64(blob);
				const resp = await forge.transcribeAudio({ data, mimeType });
				if (sessionRef.current !== session) return;
				setState('idle');
				// an empty transcript means the audio held no speech; saying so beats
				// ending the recording with nothing visible happening
				if (resp.text) onTranscript(resp.text);
				else setError('No speech detected');
			} catch (e) {
				if (sessionRef.current !== session) return;
				Sentry.captureException(e);
				setState('idle');
				setError('Transcription failed — try again');
			}
		},
		[onTranscript]
	);

	const finishRef = useRef(finish);
	finishRef.current = finish;

	const start = useCallback(
		async () => {
			if (state !== 'idle') return;
			setError(null);

			// permission gate and the source for both the waveform and the recording
			let stream: MediaStream;
			try {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			} catch {
				setError('Microphone access denied');
				return;
			}
			streamRef.current = stream;

			const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
			const audioContext: AudioContext = new AudioContextCtor();
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 1024;
			analyser.smoothingTimeConstant = 0.5;
			audioContext.createMediaStreamSource(stream).connect(analyser);
			audioContextRef.current = audioContext;
			analyserRef.current = analyser;

			let recorder: MediaRecorder;
			try {
				const mimeType = pickMimeType();
				recorder = new MediaRecorder(stream, {
					...(mimeType && { mimeType }),
					audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
				});
			} catch (e) {
				Sentry.captureException(e);
				teardownAudio();
				setError('Recording not supported in this browser');
				return;
			}
			chunksRef.current = [];
			recorder.ondataavailable = (event: BlobEvent) => {
				if (event.data.size > 0) chunksRef.current.push(event.data);
			};
			// periodic chunks so a long recording never rides on one giant buffer
			recorder.start(1000);
			recorderRef.current = recorder;

			startedAtRef.current = Date.now();
			setElapsedMs(0);
			setState('recording');
			onRecordStart?.();

			tickerRef.current = window.setInterval(() => {
				const elapsed = Date.now() - startedAtRef.current;
				setElapsedMs(elapsed);
				if (elapsed >= MAX_RECORDING_MS) {
					void finishRef.current();
				}
			}, 250);
		},
		[state, onRecordStart]
	);

	const stop = useCallback(() => {
		if (state !== 'recording') return;
		void finish();
	}, [state, finish]);

	const cancel = useCallback(() => {
		sessionRef.current++;
		const recorder = recorderRef.current;
		recorderRef.current = null;
		if (recorder && recorder.state !== 'inactive') recorder.stop();
		chunksRef.current = [];
		teardownAudio();
		clearTicker();
		setState('idle');
	}, []);

	// unmount: discard the session without firing callbacks or requests
	useEffect(() => {
		return () => {
			sessionRef.current++;
			const recorder = recorderRef.current;
			recorderRef.current = null;
			if (recorder && recorder.state !== 'inactive') recorder.stop();
			teardownAudio();
			clearTicker();
		};
	}, []);

	return { state, elapsedMs, error, analyserRef, start, stop, cancel };
};
